import React, { useMemo } from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, string } from 'prop-types'

import { addTag, clearTags, removeTag } from 'Pods/SearchPage/Store/actions/tags'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'
import I18n from 'Pods/Shared/I18n'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useMediaQuery from 'Utils/useMediaQuery'

// Inline cap ladder: 2 from lg (where the bar first shows tags), 4 from 1440px and
// 6 from xl (1600); the rest live in the "More" dropdown. 1440 is not a
// $grid-breakpoints token (useBreakpoint can't express it) — it mirrors
// $main-filters-slot-wide-min in the SCSS, hence the raw media queries.
const MAX_INLINE_TAGS = 2
const MAX_INLINE_TAGS_WIDE = 4
const MAX_INLINE_TAGS_XL = 6

const t = (key, options) => I18n.t(key, { ns: 'rails', ...options })

// Selected tags are cleared by setAssetType (user-initiated changes only), not an effect here -
// popstate restores set assetType and the hydrated tags together, and an effect-based reset
// would wipe the restored tags right back out.
function TagSuggestions({ suggestedTags = EMPTY_ARRAY }) {
  const selectedTags = useSearchPageStore(state => state.tags)

  const selectedSet = useMemo(() => new Set(selectedTags), [selectedTags])

  // Selected tags first (in selection order), then backend suggestions; deduped.
  // Set preserves insertion order, so this gives [...selected, ...unselectedSuggestions].
  const orderedTags = useMemo(
    () => Array.from(new Set([...selectedTags, ...suggestedTags])),
    [selectedTags, suggestedTags]
  )

  const isWide = useMediaQuery('(min-width: 1440px)')
  const isExtraWide = useMediaQuery('(min-width: 1600px)')

  let maxInlineTags = MAX_INLINE_TAGS
  if (isExtraWide) {
    maxInlineTags = MAX_INLINE_TAGS_XL
  } else if (isWide) {
    maxInlineTags = MAX_INLINE_TAGS_WIDE
  }

  const inlineTags = orderedTags.slice(0, maxInlineTags)
  const overflowTags = orderedTags.slice(maxInlineTags)
  const hasOverflow = overflowTags.length > 0
  const hasSelectedAnyFilters = selectedTags.length > 0

  const handleToggle = (tag) => {
    if (selectedSet.has(tag)) {
      removeTag(tag)
    } else {
      addTag(tag)
    }
  }

  const morePlaceholder = hasOverflow
    ? t('search_page.filters.tags.more_with_count', { count: overflowTags.length })
    : t('search_page.filters.tags.more')

  const dropdownContent = (
    <div className="tags-filter__dropdown">
      <div
        className="tags-filter__dropdown-grid"
        role="group"
        aria-label={t('search_page.filters.tags.tags_group_label')}
      >
        {orderedTags.map(tag => (
          <Button
            key={tag}
            type="secondary"
            size="tiny"
            isActive={selectedSet.has(tag)}
            aria-pressed={selectedSet.has(tag)}
            onClick={() => handleToggle(tag)}
            customClass="tags-filter__chip"
          >
            {tag}
          </Button>
        ))}
      </div>
      <Button
        type="ghost-primary"
        size="small"
        customClass="filter-wrapper__reset-button filter-wrapper__reset-button--inline"
        onClick={clearTags}
        disabled={!hasSelectedAnyFilters}
      >
        {t('search_page.filters.tags.reset')}
      </Button>
    </div>
  )

  return (
    <div
      className="tags-filter"
      role="group"
      aria-label={t('search_page.filters.tags.tags_group_label')}
    >
      {inlineTags.map(tag => (
        <Button
          key={tag}
          type="secondary"
          isActive={selectedSet.has(tag)}
          aria-pressed={selectedSet.has(tag)}
          onClick={() => handleToggle(tag)}
          customClass="filter-wrapper__button tags-filter__chip--inline"
        >
          {tag}
        </Button>
      ))}

      {hasOverflow && (
        <div className="tags-filter__more">
          <FiltersWrapper
            placeholder={morePlaceholder}
            content={dropdownContent}
            hasSelectedAnyFilters={hasSelectedAnyFilters}
            closingDropdownClassName="filter-wrapper"
          />
        </div>
      )}
    </div>
  )
}

TagSuggestions.propTypes = {
  suggestedTags: arrayOf(string),
}

export default TagSuggestions
